import type { PackageAddonDisplayModel, PackageAddonUpsertModel } from './packageAddons';
import type {
  PackageItemCreateModel,
  PackageItemDetailsDisplayModel,
  PackageItemDisplayModel,
  PackageItemUpdateModel,
} from './packageItems';

export interface IPackageModelProperties {
  id: number;
  name: string;
  retailCost: number;
  printCost: number;
  altName: string;
  altDescription: string;
  thumbnailUrl: string;
  allowConsumerCrop: boolean;
  allowConsumerImageChange: boolean;
  /**
   * True when every item in the package is digital (ProductLookup.ShipTier = "0"), so the package
   * has nothing to ship. Server-populated; absent on payloads that predate the flag, hence optional.
   */
  isDigital?: boolean;
}

export interface PackageCreateModel {
  // studioJobPreference: any;
  name: string;
  altName: string;
  retailCost: number;
  sortIndex: number;
  thumbnailUrl: string;
  packageItems: PackageItemCreateModel[];
  packageSetId: number | null;
  studioId: number | null;
  type: string;
  altDescription: string;
  packageAddon?: PackageAddonUpsertModel;

  /**
   * Front end only - used to push thumbnail to server
   */
  file?: File;
  printCost?: number;
}

export interface PackageUpdateModel {
  id: number;
  altName: string;
  altDescription: string;
  thumbnailUrl: string | null;
  retailCost: number;
  packageAddon?: PackageAddonUpsertModel;
  packageItems: PackageItemUpdateModel[];

  /**
   * Front end only - used to push thumbnail to server
   */
  file?: File;

  printCost?: number;
}

type PackageUpsertModel = PackageCreateModel | PackageUpdateModel;

export interface BasePackageDisplayModel<TPackageItemDisplayModel> {
  id: number;
  packageSetId?: number;
  previousVersionPackageId?: number;

  name: string;
  retailCost: number;
  altName: string;
  altDescription: string;
  thumbnailUrl: string;
  sortIndex: number;
  allowConsumerImageChange: boolean;
  allowConsumerCrop: boolean;
  printCost: number;
  items: TPackageItemDisplayModel[];
  type: string;
  packageAddon?: PackageAddonDisplayModel;
  /**
   * Front end only - used to push thumbnail to server
   */
  file?: File;

  version?: string;
  versionNotes?: string;
  isCurrentVersion?: boolean;

  isDeleted?: boolean;

  updatedAt: string;
}

export type PackageDisplayModel = BasePackageDisplayModel<PackageItemDisplayModel>;

export type PackageDetailsDisplayModel = BasePackageDisplayModel<PackageItemDetailsDisplayModel>;

export enum PackageTypes {
  SPORT = 'sport',
  SCHOOL = 'school',
  //Excluded
  //UNDEFINED = null,
}

export type PackageStatus = {
  usedInUnsubmittedCarts: boolean;
  /** Entry-built carts not yet placed as an order. */
  unplacedCartCount: number;
  /** Consumer-purchased carts whose order has not been sent to the lab yet. */
  purchasedCartCount: number;
  /** The still-locked subset of purchasedCartCount: the carts an update will unlock. */
  lockedPurchasedCartCount: number;
};

/**
 * Strategy for deciding whether cart packages using the package should be updated.
 */
export type PackageMutationStrategy = 'None' | 'AllUnsubmitted' | 'NewOnly';
